import React from 'react'
import {
  useBasketItemList,
  useUpdateStrings,
  myCartApi,
  LocalLabelsProvider,
} from '@sentecacommerce-theme/lib'
import { Summary, EmptyBasket } from './Components'
import {
  BasketPreviewItem,
  BasketPreviewItemPlaceholder,
  Heading,
  IconChevronRight,
} from '../Common'
import { BasketProps, BasketLabels } from './basket.props'
import { Link, useBasketItemErrorRef } from '@sentecacommerce-theme/lib'
import styled from 'styled-components'

export const Basket = ({ labels, title }: BasketProps) => {
  const { lineItems, isLoading } = useBasketItemList()
  const { data: compact } = myCartApi.useGetCompact()

  const hasItems = Boolean(lineItems?.length)

  const { errorRef, getFirstError } = useBasketItemErrorRef()

  return (
    <LocalLabelsProvider<BasketLabels> labels={labels}>
      <Wrapper>
        <div className="row align-items-center">
          <div className="col col-lg-4 d-none d-lg-flex"></div>
          <div className="col col-12 col-lg-4">
            <Heading
              isCentered
              topTitle
              as="h1"
              data={title}
              className="my-5"
            />
          </div>
          <div className="col col-12 col-lg-4">
            <Link href="/" passHref>
              <ContinueShopping>
                <IconChevronRight className="mr-2 ml-lg-auto" />
                <span>{labels?.continueWithShopping}</span>
              </ContinueShopping>
            </Link>
          </div>
        </div>
        {!isLoading && !hasItems && <EmptyBasket />}

        {(hasItems || isLoading) && (
          <Container>
            <MobileSummary>
              <Box>
                <Summary hideCode getFirstElementWithError={getFirstError} />
              </Box>
            </MobileSummary>
            <ItemsContainer>
              <ItemsList>
                {isLoading &&
                  new Array(Math.min(compact?.lineItemsCount || 2, 8))
                    .fill('')
                    .map((_, i) => {
                      return (
                        <article
                          key={`ItemPlaceholder__${i}`}
                          className="col-12"
                        >
                          <Box>
                            <BasketPreviewItemPlaceholder />
                          </Box>
                        </article>
                      )
                    })}

                {!isLoading &&
                  lineItems?.map((item) => (
                    <article key={`Product__${item?._id}`} className="col-12">
                      <BasketPreviewItem
                        errorRef={errorRef}
                        productNumberLabel={labels.productNumberLabel}
                        data={item}
                      />
                    </article>
                  ))}
              </ItemsList>
            </ItemsContainer>
            <SummaryContainer>
              <SummarySticky>
                <Box>
                  <Summary getFirstElementWithError={getFirstError} />
                </Box>
              </SummarySticky>
            </SummaryContainer>
          </Container>
        )}
      </Wrapper>
    </LocalLabelsProvider>
  )
}

const Wrapper = styled.div.attrs({
  className: 'container',
})``

const Container = styled.section.attrs({
  className: 'row g-4',
})``

const Box = styled.div.attrs({
  className: 'p-4',
})`
  background: ${({ theme }) => theme.palette.secondary};
  border-radius: 1.4rem;
  box-shadow: 0 0 0.8rem ${({ theme }) => theme.palette.lightgrey};
`

const ItemsContainer = styled.div.attrs({
  className: 'col-12 col-md-7 col-lg-8',
})``

const ItemsList = styled.div.attrs({
  className: 'row g-4',
})``

const ContinueShopping = styled.a.attrs({
  className: 'd-flex align-items-center mb-5 mt-lg-4 mb-lg-4',
})`
  color: ${({ theme }) => theme.palette.greyTitle};
  white-space: nowrap;

  & > svg {
    width: 1.2rem;
    transform: rotate(180deg);
  }
`

const SummarySticky = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    position: sticky;
    top: 10.4rem;
  }
`

const SummaryContainer = styled.aside.attrs({
  className: 'col-12 col-md-5 col-lg-4',
})`
  & div {
    font-size: 1.5rem;
  }
`

const MobileSummary = styled.div.attrs({
  className: 'col-12 d-md-none',
})``
